import { Component } from '@angular/core';
import {forkJoin, of} from 'rxjs';
import {mergeMap} from 'rxjs/operators';
import {ShippingItemInfo, ShippingInfo, UserStorageInfo} from 'src/app/models/models';
import {Category, Item, Shipping_Item, Shipping_Item_Batch, Store} from 'src/app/models/RestModels';
import {BaseComponent} from '../base/base.component';


interface Received_Qty
{
	box_content_id?:number;
	item_id?:number;
	qty:number;
}

interface ShippingItemInfoExpanded extends ShippingItemInfo
{
	show_batches?: boolean;
}

@Component({
	selector: 'app-view-shipping',
	templateUrl: './view-shipping.component.html',
	styleUrls: ['./view-shipping.component.css']
})
export class ViewShippingComponent extends BaseComponent {

	item_dictionary:Record<number,Item> = {};
	category_dictionary:Record<number,Category> = {};
	selected_category_id:number = null;
	item_list:Item[];
	category_list:Category[] = [];
	selected_item_id:number = null;
	shipping_info:ShippingInfo = null;
	store_list:Store[] = [];
	store_dictionary:Record<number,Store> = {};
	received_qty:Record<string,Received_Qty> = {};

	available_storages: UserStorageInfo[] = [];
	show_storage_modal: boolean = false;
	custom_storage_item: Partial<ShippingItemInfo> | null = null;
	custom_storage_rows: { storage_id: number | null; qty: number }[] = [];

	total_pallets:number = 0;
	total_boxes:number = 0;
	total_items:number = 0;
	total_received_items:number = 0;
	total_individual_boxes: number = 0;

	ngOnInit()
	{
		this.subs.sink = this.route.paramMap.subscribe( paramMap =>
		{
			this.received_qty = {};
			this.is_loading = true;

			this.subs.sink = forkJoin
			({
				shipping_info : this.rest.shipping_info.get(paramMap.get('shipping_id')),
				store : this.rest.store.search({limit:9999,sort_order:['name_ASC']})
			})
			.pipe
			(
				mergeMap((response)=>
				{
					response.store.data.forEach((store)=>this.store_dictionary[store.id]=store);

					let tmp_category_ids:number[] = response.shipping_info.items
					.map((sii:ShippingItemInfo)=>
					{
						return sii.item?.category_id || null;
					});

					let	category_ids:number[] = tmp_category_ids.filter(i=>i!==null);

					return forkJoin
					({
						shipping_info: of( response.shipping_info ),
						category: this.rest.category.search({limit:99999,csv:{id:category_ids},sort_order:['name_ASC']})
					})
				})
			)
			.subscribe((responses)=>
			{
				this.is_loading = false;
				responses.category.data.forEach(c=>this.category_dictionary[c.id]=c);

				this.total_pallets = 0;
				this.total_boxes = 0;
				this.total_items = 0;
				this.total_received_items = 0;
				this.total_individual_boxes= 0;

				if( this.rest.current_permission.shipping_receive_type == 'VALIDATE' )
				{
					responses.shipping_info.items.forEach((sii:ShippingItemInfoExpanded)=>
					{
						sii.shipping_item.received_qty = sii.shipping_item.qty;
						// Also set batch received_qty to match qty for VALIDATE mode
						if (sii.shipping_item_batches && sii.shipping_item_batches.length > 0)
						{
							sii.show_batches = true;
							sii.shipping_item_batches.forEach((b: Shipping_Item_Batch) =>
							{
								b.received_qty = b.qty;
								b.shrinkage_qty = 0;
							});
						}
					});
				}
				else
				{
					// Initialize batch received_qty/shrinkage_qty to 0 for CAPTURE_QTY mode
					responses.shipping_info.items.forEach((sii:ShippingItemInfo)=>
					{
						if (sii.shipping_item_batches && sii.shipping_item_batches.length > 0)
						{
							sii.shipping_item_batches.forEach((b: Shipping_Item_Batch) =>
							{
								b.batch = '';
								b.expiration_date = '';
								if (b.received_qty === null) b.received_qty = 0;
								if (b.shrinkage_qty === null) b.shrinkage_qty = 0;
							});
						}
					});
				}

				responses.shipping_info.items.forEach((sii:ShippingItemInfo)=>
				{
					this.total_items	+= sii.shipping_item.qty;
					this.total_received_items += sii.shipping_item.received_qty;
				});

				this.calculateReceivedItems();

				let to_store_id = responses.shipping_info.shipping.to_store_id || this.rest.current_user.store_id;

				this.subs.sink = this.rest.user_storage_info.search({
					eq: { user_id: this.rest.current_user.id },
					limit: 9999
				}).subscribe(storage_res => {
					this.available_storages = storage_res.data.filter(us => us.storage.store_id == to_store_id);

					responses.shipping_info.items.forEach(i=>{
						if (!i.storage_action) {
							i.storage_action = 'SIN_ASIGNAR';
							i.storage_quantities = [];
						}
					});
				});

				this.shipping_info = responses.shipping_info;
				this.item_list = [];
				this.category_list = responses.category.data;
			},(error)=>this.showError(error));
		});
	}

	calculateReceivedItems()
	{
		if( !this.shipping_info )
			return;

		this.total_received_items = 0;
		this.shipping_info.items.forEach((sii:ShippingItemInfo)=>
		{
			console.log( sii );
			this.total_received_items += sii.shipping_item.received_qty;
		});
	}

	save(evt:any)
	{
		// Validate batch totals match item totals for items with batches
		for (let sii of this.shipping_info.items)
		{
			if (sii.shipping_item_batches && sii.shipping_item_batches.length > 0)
			{
				let batchReceivedTotal = sii.shipping_item_batches.reduce((sum, b) => sum + (b.received_qty || 0), 0);
				let batchShrinkageTotal = sii.shipping_item_batches.reduce((sum, b) => sum + (b.shrinkage_qty || 0), 0);

				if (batchReceivedTotal !== sii.shipping_item.received_qty)
				{
					this.showError(`El total recibido de lotes (${batchReceivedTotal}) no coincide con el total recibido del artículo (${sii.shipping_item.received_qty}) para "${sii.item?.name}"`);
					return;
				}

				if (batchShrinkageTotal !== (sii.shipping_item.shrinkage_qty || 0))
				{
					this.showError(`El total de merma de lotes (${batchShrinkageTotal}) no coincide con la merma del artículo (${sii.shipping_item.shrinkage_qty || 0}) para "${sii.item?.name}"`);
					return;
				}
			}
		}

		// Validate storage assignments
		for (let item of this.shipping_info.items) {
			let r_qty = item.shipping_item.received_qty || 0;
			if (item.storage_action === 'PERSONALIZADO') {
				let sum = (item.storage_quantities || []).reduce((s, row) => s + (row.qty || 0), 0);
				if (sum !== r_qty) {
					this.showError(`El artículo ${item.item?.name} tiene un asignamiento de almacén personalizado que no coincide con la cantidad recibida.`);
					return;
				}
			} else if (item.storage_action !== 'SIN_ASIGNAR') {
				item.storage_quantities = [{ storage_id: Number(item.storage_action), qty: r_qty }];
			} else {
				item.storage_quantities = [];
			}
		}

		// Build items array with batch details
		let items = this.shipping_info.items.map((sii) => {
			let item: any = {
				shipping_item: sii.shipping_item,
				storage_quantities: sii.storage_quantities
			};

			if (sii.shipping_item_batches && sii.shipping_item_batches.length > 0)
			{
				item.shipping_item_batches = sii.shipping_item_batches.map(b => ({
					id: b.id,
					batch: b.batch,
					expiration_date: b.expiration_date,
					qty: b.qty,
					received_qty: b.received_qty,
					shrinkage_qty: b.shrinkage_qty
				}));
			}

			return item;
		});

		let params:Record<string,any> = {
			shipping_id: this.shipping_info.shipping.id,
			items: items
		};

		this.is_loading = true;

		this.subs.sink = this.rest.updatePath('receiveShipping',params)
		.subscribe((response)=>
		{
			this.showSuccess('Se guardo la información correctamente');
			this.location.back();
			this.router.navigate(['/list-shipping']);
		},(error)=>this.showError(error));
	}

	onClickedCheckBox(evt:Event, shipping_item:Shipping_Item)
	{
		let t = evt.currentTarget as HTMLInputElement;
		shipping_item.received_qty = t.checked ? 1 : 0;
	}

	// Batch helpers
	hasBatches(sii: Partial<ShippingItemInfo>): boolean
	{
		return sii.shipping_item_batches && sii.shipping_item_batches.length > 0;
	}

	toggleBatches(sii: any): void
	{
		sii.show_batches = !sii.show_batches;
	}

	getBatchTotal(sii: Partial<ShippingItemInfo>, field: 'qty' | 'received_qty' | 'shrinkage_qty'): number
	{
		if (!sii.shipping_item_batches) return 0;
		return sii.shipping_item_batches.reduce((sum, b) => sum + (b[field] || 0), 0);
	}

	onBatchQtyChange(sii: Partial<ShippingItemInfo>): void
	{
		// Update the shipping_item totals based on batch totals
		if (sii.shipping_item_batches && sii.shipping_item_batches.length > 0)
		{
			sii.shipping_item.received_qty = this.getBatchTotal(sii, 'received_qty');
			sii.shipping_item.shrinkage_qty = this.getBatchTotal(sii, 'shrinkage_qty');
		}
		this.calculateReceivedItems();
	}

	isBatchItem(sii: Partial<ShippingItemInfo>): boolean
	{
		return sii.item?.batch_option && sii.item.batch_option !== 'NONE';
	}

	onStorageActionChange(item: Partial<ShippingItemInfo>, event: any) {
		if (item.storage_action === 'PERSONALIZADO') {
			this.openStorageModal(item);
		} else {
			item.storage_quantities = [];
		}
	}

	openStorageModal(item: Partial<ShippingItemInfo>) {
		this.custom_storage_item = item;
		this.custom_storage_rows = item.storage_quantities?.length ? [...item.storage_quantities] : [{ storage_id: null, qty: item.shipping_item.received_qty || 0 }];
		this.show_storage_modal = true;
	}

	addStorageRow() {
		this.custom_storage_rows.push({ storage_id: null, qty: 0 });
	}

	removeStorageRow(index: number) {
		if (this.custom_storage_rows.length > 1) {
			this.custom_storage_rows.splice(index, 1);
		}
	}

	confirmCustomStorage() {
		let r_qty = this.custom_storage_item.shipping_item.received_qty || 0;
		if (this.custom_storage_item.shipping_item_batches && this.custom_storage_item.shipping_item_batches.length > 0) {
			r_qty = this.custom_storage_item.shipping_item_batches.reduce((sum, b) => sum + (b.received_qty || 0), 0);
		}

		let total = this.custom_storage_rows.reduce((sum, r) => sum + (r.qty || 0), 0);

		if (total !== r_qty) {
			this.showError(`La cantidad asignada (${total}) no coincide con la cantidad recibida acumulada (${r_qty}).`);
			return;
		}

		for (let row of this.custom_storage_rows) {
			if (!row.storage_id) {
				this.showError('Debe seleccionar un almacén para todas las filas.');
				return;
			}
		}

		this.custom_storage_item.storage_quantities = [...this.custom_storage_rows];
		this.show_storage_modal = false;
		this.custom_storage_item = null;
	}

	cancelCustomStorage() {
		this.custom_storage_item.storage_action = 'SIN_ASIGNAR';
		this.custom_storage_item.storage_quantities = [];
		this.show_storage_modal = false;
		this.custom_storage_item = null;
	}
}
